<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="case-study-title" :field="fields?.title" tag="h2" />
      <JssRichText class="case-study-desc" :field="fields?.subtitle" />
      <div class="case-study-content" v-if="fields?.list?.length > 0">
        <div
          class="case-study-content-list"
          :class="{ 'case-study-content-list--single': fields?.list?.length === 1 }"
        >
          <div class="case-study-content-list-item" v-for="item in fields?.list" :key="item.id">
            <AdaptiveImage
              class="case-study-content-list-item-image"
              :field="item?.fields?.image"
              :lazy-load="false"
              :enableScrollDetection="false"
              :show="true"
            />
            <JssRichText
              class="case-study-content-list-item-title"
              :field="item?.fields?.title"
              tag="h3"
            />
            <JssRichText class="case-study-content-list-item-desc" :field="item?.fields?.desc" />
            <SiteButton
              class="case-study-content-list-item-button"
              v-bind="item?.fields?.button"
              @click="openModal(item)"
            />
          </div>
        </div>
      </div>
      <modal closable ref="modalRef" class="case-study-modal">
        <div id="finance-div" class="case-study-modal-container">
          <JssRichText
            class="case-study-modal-container-title"
            :field="item?.fields?.modalTitle"
            tag="h3"
          />
          <AdaptiveImage
            class="case-study-modal-container-image"
            :field="item?.fields?.modalImage"
            :is-background="false"
          />
          <JssRichText
            class="case-study-modal-container-subtitle"
            :field="item?.fields?.modalSubtitle"
            tag="h3"
          />
          <JssRichText
            class="case-study-modal-container-body"
            :field="item?.fields?.modalBody"
            tag="h3"
          />
        </div>
      </modal>
    </ContentGrid>
  </div>
</template>

<script>
import { computed, reactive, toRefs } from 'vue';
import utils from '@/utils';

export default {
  name: 'CaseStudy',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const state = reactive({
      modalRef: null,
      item: null,
    });

    const methods = {
      openModal(item) {
        if (item.fields.button.fields.link?.value?.href) {
          return false;
        }
        state.item = item;
        state.modalRef.open();
      },
    };

    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'case-study'));

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    return {
      ...toRefs(state),
      rootClass,
      rootStyle,
      ...methods,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.case-study {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    text-align: center;
  }

  &-desc {
    font-size: 14px;
    line-height: 1.6;
    color: $black;
    margin-top: 8px;
  }

  &-content {
    width: 100%;
    margin-top: 24px;
  }

  &-content-list {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &-content-list-item {
    position: relative;
    display: flex;
    flex-direction: column;
    background: $white;
    border-radius: 24px;
    padding: 162px 0 24px;
    box-shadow: 0 24px 60px rgba(24, 53, 143, 0.12);

    &-image {
      position: absolute;
      left: 0;
      top: 0;
      width: 240px;
      height: 130px;
      object-fit: cover;
      border-radius: 24px 24px 0 0;
      margin: 0;
    }

    &-title {
      font-size: 18px;
      font-weight: 600;
      color: $black;
      margin-bottom: 12px;
      line-height: 1.4;
      padding: 0 24px;
    }

    &-desc {
      font-size: 14px;
      color: rgba(13, 21, 54, 0.68);
      line-height: 1.6;
      padding: 0 24px;
    }

    &-button {
      margin-top: 12px;
      margin-left: 24px;
    }
  }

  &-modal {
    width: 100%;
    &-container {
      &-title {
        font-size: 20px;
        font-weight: 600;
        line-height: 1.4;
      }
      &-image {
        width: 100%;
        height: auto;
        margin-top: 16px;
      }
      &-subtitle {
        font-size: 14px;
        line-height: 1.4;
        color: $grey-black;
        margin-top: 16px;
        font-weight: 600;
      }
      &-body {
        font-size: 12px;
        line-height: 1.4;
        color: $grey-black;
        margin-top: 8px;
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-desc {
      font-size: 18px;
    }

    &-content {
      margin-top: 40px;
    }

    &-content-list {
      flex-direction: row;
      gap: 32px;

      &--single {
        justify-content: center;
      }
    }

    &-content-list-item {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 162px 0 32px;

      .case-study-content-list--single & {
        flex: none;
        width: 50%;
      }

      &-image {
        position: absolute;
        left: 0;
        top: 0;
        height: 130px;
        margin: 0;
      }

      &-title {
        font-size: 20px;
        margin-bottom: 16px;
        padding: 0 32px;
      }

      &-desc {
        font-size: 15px;
        padding: 0 32px;
      }

      &-button {
        margin-top: auto;
        margin-left: 32px;
      }
    }

    &-modal {
      .e-modal__content {
        width: 70vw;
      }
    }
  }
}
</style>
